import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { SecondaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { ClickAndCollectViewTabsProps } from './ClickAndCollectViewTabs.types';

// Config
import { VIEW_TYPE_LIST, VIEW_TYPE_MAP } from '@constants/clickCollectConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { setCollectionPointViewType } from '@redux/actions/click-and-collect/clickAndCollectActions';
import { getActiveViewTypeSelector } from '@redux/reducers/click-and-collect/clickAndCollectSelector';

// Components
import Container from '@components/container';

// Styles
import styles from './click-and-collect-view-tabs.scss';

const ClickAndCollectViewTabs = ({ disabled = false }: ClickAndCollectViewTabsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const activeViewType = useSelector(getActiveViewTypeSelector);
  const { desktopResolution } = useWindowSize();

  const getButtonClassnames = (id: string) =>
    cx(styles.button, 'mb-0', {
      [styles.selectedButton]: activeViewType === id,
    });

  const handleTabClick = (id: string) => dispatch(setCollectionPointViewType(id));

  const renderTab = (tab: { id: string; label: string }, disabled: boolean) => {
    const isSelected = activeViewType === tab.id;
    const selectedString = isSelected ? 'selected' : 'un-selected';

    return (
      <SecondaryButton
        key={tab.id}
        className={getButtonClassnames(tab.id)}
        data-testid={`click-collect-button-${tab.id}`}
        disabled={disabled}
        onClick={() => handleTabClick(tab.id)}
        size="small"
      >
        <span aria-hidden="true">{tab.label}</span>
        <span className="visually-hidden">{`${tab.label} ${selectedString}`}</span>
      </SecondaryButton>
    );
  };

  const tabs = [
    {
      id: VIEW_TYPE_LIST,
      label: 'List view',
    },
    {
      id: VIEW_TYPE_MAP,
      label: 'Map view',
    },
  ];

  return (
    <Container
      className={styles.list}
      flex={{ gap: '2' }}
      marginBottom={desktopResolution ? '3' : '2'}
      tag="nav"
      testId="click-collect-view-tabs-container"
    >
      {tabs.map((tab) => renderTab(tab, disabled))}
    </Container>
  );
};

export default ClickAndCollectViewTabs;
